/**
 * Eric 2026-06-25 (iter117 — Session A1) — Panneau d'import de transcription
 * Fireflies / AssemblyAI. Extrait de VideoDetailPage.js dans le cadre du
 * refacto P0 (5818 lignes → composants modulaires).
 *
 * Affiché uniquement quand :
 *   - l'utilisateur est éditeur de contenu (canEditContent)
 *   - l'onglet courant de transcription est "fireflies"
 *   - aucune transcription Fireflies n'a encore été importée
 *
 * Comporte :
 *   - Un état d'attente quand la fusion est bloquée (Whisper pas prêt).
 *   - Une zone de glisser-déposer fichier (.docx, .txt, .md).
 *   - Un input fichier classique.
 *   - Une zone de texte « coller » + bouton Fusionner.
 *
 * Les états partagés (crossValidating / crossValidateText / crossValidateResult)
 * restent dans VideoDetailPage car ils sont aussi consommés ailleurs (bouton
 * « Refaire la fusion » côté Fusionnée).
 *
 * Permissions : la condition `show` est calculée côté parent à partir de
 * `canEditContent(user)` — aucune logique de rôle inline ici (cf. règle de
 * non-régression permission drift, scripts/check_permission_drift.sh).
 */
import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { SpinnerGap, ArrowClockwise, FileText } from "@phosphor-icons/react";
import { Button } from "../ui/button";

export const FirefliesImportPanel = ({
  show,
  fusionBlockedReason,
  importBlockedReason,
  lang,
  api,
  videoId,
  crossValidating,
  setCrossValidating,
  crossValidateText,
  setCrossValidateText,
  crossValidateResult,
  setCrossValidateResult,
  loadAllTranscripts,
  setTranscriptSubTab,
  pollMergedTranscript,
}) => {
  if (!show) return null;

  // Vincent B751 (Eric 2026-02-17) — L'import Fireflies était bloqué dès que
  // Whisper tournait encore (« Patientez : la transcription Whisper doit
  // être terminée avant l'import Fireflies »). Or l'endpoint backend
  // /cross-validate-transcript-file?defer_fusion=true accepte l'import EN
  // PARALLÈLE de Whisper depuis Vincent bug #86. On bloque désormais
  // UNIQUEMENT sur `importBlockedReason` (pipeline failed) — la fusion
  // reste bloquée via `fusionBlockedReason` sur le bouton « Refaire la
  // fusion » plus bas.
  if (importBlockedReason) {
    return (
      <div className="p-10 text-center" data-testid="fireflies-pipeline-pending">
        <SpinnerGap size={36} className="mx-auto mb-3 animate-spin text-amber-500" />
        <p className="text-sm text-amber-900 font-medium max-w-md mx-auto">{importBlockedReason}</p>
      </div>
    );
  }

  // Handler partagé entre drag&drop et input file.
  const uploadFile = async (f) => {
    setCrossValidating(true);
    setCrossValidateResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      // Vincent bug #86 (2026-05-08) — defer_fusion=true : on importe la
      // transcription Fireflies pour consultation immédiate sans déclencher
      // la fusion. L'utilisateur clique ensuite « Refaire la fusion ».
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/cross-validate-transcript-file?source=fireflies&defer_fusion=true`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      setCrossValidateResult(data);
      await loadAllTranscripts();
      setTranscriptSubTab("fireflies");
      setCrossValidating(false);
      toast.success(
        lang === "fr"
          ? "Transcription Fireflies importée. Cliquez « Refaire la fusion » pour lancer la fusion."
          : "Fireflies imported. Click 'Re-run merge' to start fusion.",
        { duration: 8000 }
      );
    } catch (err) {
      console.error(err);
      toast.error(
        lang === "fr"
          ? `Erreur : ${err.response?.data?.detail || err.message}`
          : `Error: ${err.response?.data?.detail || err.message}`
      );
      setCrossValidating(false);
    }
  };

  return (
    <div className="px-3 py-3 border-b border-zinc-100" data-testid="cross-validate-section">
      <p className="text-xs font-semibold text-zinc-700 mb-1 flex items-center gap-1.5">
        <ArrowClockwise size={14} className="text-[#002FA7]" />
        {lang === "fr" ? "Importer une transcription Fireflies" : "Import a Fireflies transcript"}
      </p>
      <div className="space-y-2">
        <p className="text-[10px] text-zinc-500">
          {lang === "fr"
            ? "Importez un fichier .docx (recommandé pour les exports Fireflies) ou .txt, ou collez le texte. Whisper + Fireflies sont fusionnés pour optimiser le jargon financier québécois et les noms propres."
            : "Upload a .docx (recommended for Fireflies exports) or .txt file, or paste the text. Whisper + Fireflies are merged to optimize Québec financial jargon and proper nouns."}
        </p>
        {/* B774 v2 (Eric 2026-02-17 SPEC STRICK PROD) — Le drag&drop sur un
            <label> qui contient un <input type="file"> caché est cassé sur
            Chrome/Firefox (le browser catch le drop AVANT React et déclenche
            un download au lieu du onDrop). Fix : un <div> pour la zone de
            drop, un <input> hors du div avec ref, et un onClick manuel. */}
        <div
          role="button"
          tabIndex={0}
          className="flex flex-col items-center gap-2 text-xs cursor-pointer text-[#002FA7] border-2 border-dashed border-[#002FA7]/40 rounded-lg px-3 py-4 hover:bg-[#002FA7]/5 hover:border-[#002FA7] transition-all"
          data-testid="cross-validate-file-label"
          onClick={() => document.querySelector('[data-testid="cross-validate-file-input"]')?.click()}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") document.querySelector('[data-testid="cross-validate-file-input"]')?.click(); }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.classList.add("bg-[#002FA7]/10");
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.classList.remove("bg-[#002FA7]/10");
          }}
          onDrop={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.classList.remove("bg-[#002FA7]/10");
            const f = e.dataTransfer.files?.[0];
            if (!f) {
              toast.error(lang === "fr" ? "Aucun fichier détecté" : "No file detected");
              return;
            }
            const validExt = /\.(docx|txt|md)$/i.test(f.name);
            if (!validExt) {
              toast.error(
                lang === "fr"
                  ? "Format non supporté. Utilisez .docx, .txt ou .md"
                  : "Unsupported format. Use .docx, .txt or .md"
              );
              return;
            }
            await uploadFile(f);
          }}
        >
          <FileText size={20} />
          <span className="font-medium">
            {lang === "fr" ? "Glisser-déposer un fichier ici" : "Drag and drop a file here"}
          </span>
          <span className="text-[10px] text-zinc-500">
            {lang === "fr"
              ? "ou cliquez pour parcourir (.docx, .txt, .md)"
              : "or click to browse (.docx, .txt, .md)"}
          </span>
        </div>
        <input
          type="file"
          accept=".docx,.txt,.md"
          className="hidden"
          data-testid="cross-validate-file-input"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            await uploadFile(f);
            e.target.value = "";
          }}
        />
        <div className="flex items-center gap-2 text-[10px] text-zinc-400">
          <span className="flex-1 border-t border-zinc-200"></span>
          <span>{lang === "fr" ? "ou collez ci-dessous" : "or paste below"}</span>
          <span className="flex-1 border-t border-zinc-200"></span>
        </div>
        <textarea
          className="w-full h-24 text-xs border border-zinc-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
          placeholder={
            lang === "fr"
              ? "Collez la transcription Fireflies ici..."
              : "Paste Fireflies transcript here..."
          }
          value={crossValidateText}
          onChange={(e) => setCrossValidateText(e.target.value)}
          data-testid="cross-validate-input"
        />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            disabled={!crossValidateText.trim() || crossValidating}
            onClick={async () => {
              setCrossValidating(true);
              setCrossValidateResult(null);
              try {
                // B751 v2 (Eric 2026-02-17 SPEC STRICK PROD) — Import parallèle :
                // on envoie defer_fusion=true pour que le paste fonctionne PENDANT
                // Whisper. La fusion se déclenche ensuite via « Refaire la fusion ».
                const { data } = await axios.post(
                  `${api}/api/videos/${videoId}/cross-validate-transcript?defer_fusion=true`,
                  { secondary_text: crossValidateText, source: "fireflies" },
                  { withCredentials: true }
                );
                setCrossValidateResult(data);
                setCrossValidateText("");
                await loadAllTranscripts();
                setTranscriptSubTab && setTranscriptSubTab("fireflies");
                toast.success(
                  lang === "fr"
                    ? "Transcription Fireflies importée. Cliquez « Refaire la fusion » quand Whisper sera terminé."
                    : "Fireflies imported. Click 'Re-run merge' once Whisper is done.",
                  { duration: 8000 },
                );
              } catch (err) {
                console.error(err);
                toast.error(
                  lang === "fr"
                    ? `Erreur : ${err.response?.data?.detail || err.message}`
                    : `Error: ${err.response?.data?.detail || err.message}`
                );
              } finally {
                setCrossValidating(false);
              }
            }}
            className="gap-1.5 text-xs px-3 py-1.5 min-w-[110px] bg-[#002FA7] text-white hover:bg-[#001f70] disabled:bg-zinc-300 disabled:text-zinc-500"
            data-testid="cross-validate-btn"
          >
            {crossValidating ? (
              <>
                <SpinnerGap size={12} className="animate-spin" />
                <span>{lang === "fr" ? "Fusion…" : "Merging…"}</span>
              </>
            ) : (
              <>
                <ArrowClockwise size={12} />
                <span>{lang === "fr" ? "Fusionner" : "Merge"}</span>
              </>
            )}
          </Button>
          {crossValidateResult && (
            <span className="text-[10px] text-teal-600">{crossValidateResult.message}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default FirefliesImportPanel;
